<template>
  <div class="ui basic segment" id="home-wrapper">
    <div id="home">
      <div id="header">
        <h2 class="ui header">
          {{ $t('HOME_HEADER', { productName: $t('PRODUCT_NAME') || 'Cuesta' }) }}
        </h2>
        <p v-html="$t('HOME_BODY', { productName: $t('PRODUCT_NAME') || 'Cuesta' })" />
      </div>
      <div id="search">
        <div class="ui fluid scrolling category search">
          <div class="ui icon input">
            <input
              class="prompt"
              type="text"
              :placeholder="$t('SEARCH')"
              v-model="searchTerm"
              @focus="showResults = true"
              @blur="showResults = false"
            />
            <i class="search icon"></i>
          </div>
          <transition
            enter-active-class="animate__animated animate__fadeInDown"
            leave-active-class="animate__animated animate__fadeOutUp"
          >
            <div
              class="results"
              style="display: block !important;"
              v-if="searchTerm && showResults"
            >
              <div
                class="category"
                v-for="(results, name) in searchResults"
                :key="name"
                v-if="foundSomeResults"
              >
                <div class="name">
                  <div class="capitalize-fl" style="position: sticky; top: 20px;">
                    {{ name }}
                  </div>
                </div>
                <div class="results">
                  <router-link
                    class="result"
                    v-for="r in results"
                    :to="r.original.route || {}"
                    :key="r.original.id"
                  >
                    <div class="content">
                      <div class="title" v-html="r.string"></div>
                      <div class="description">{{ r.original.detail }}</div>
                    </div>
                  </router-link>
                </div>
              </div>
              <div class="message empty" v-if="!foundSomeResults">
                <div class="header">{{ $t('NO_MATCH_HEADER') }}</div>
                <div class="description">{{ $t('NO_MATCH', { term: searchTerm }) }}</div>
              </div>
            </div>
          </transition>
        </div>
      </div>
      <div id="lists">
        <component
          :is="l.componentName"
          v-bind="l.params"
          v-for="(l, idx) in lists"
          v-if="l.componentName"
          :key="idx"
        />
        <div class="ui visible info icon message" id="no-conf" v-if="noListsConfigured">
          <i class="user cog icon"></i>
          <div class="content">
            <div class="header">
              {{ $t('NO_LANDING_PAGE_CONFIGURATION_HEADER') }}
            </div>
            <p v-html="$t('NO_LANDING_PAGE_CONFIGURATION_BODY')" />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss">
#search .results .match-highlight {
  text-decoration: underline;
}
</style>

<style lang="scss" scoped>
#home-wrapper {
  padding-left: 10px;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

#home {
  margin: auto;
  width: 80%;
  display: grid;
  grid-gap: 30px;
  grid-template-rows: auto;
  grid-template-columns: auto;
  grid-template-areas:
    'intro'
    'search'
    'lists';
}

#header {
  grid-area: intro;
}

#search {
  grid-area: search;
  margin-top: 30px;
  margin-bottom: 30px;

  .icon.input {
    width: 100%;
  }

  .results {
    span {
      text-decoration: underline;
      color: green;
    }
  }
}

#lists {
  grid-area: lists;
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  grid-template-rows: auto;
  grid-gap: 20px;

  .message {
    opacity: 0.3;
    grid-column: 1 / -1; // span all columns
  }
}
</style>

<script>
// @flow
import Vue from 'vue'
import _ from 'lodash'
import FuzzyFilterer from '../Mixins/FuzzyFilterer'
import { Flow, Field, Application, Table, Group, User } from 'services'

export default {
  name: 'home',
  components: {},
  mixins: [FuzzyFilterer],

  data() {
    return {
      showResults: false,
      lists: []
    }
  },

  computed: {
    noListsConfigured() {
      return (
        !this.lists ||
        this.lists.length === 0 ||
        _.find(this.lists, l => l.componentName) === undefined
      )
    },
    foundSomeResults() {
      return !_.isEmpty(this.searchResults)
    },
    searchResults() {
      return _.groupBy(this.filteredItems, 'original.type')
    },
    items() {
      return _.concat(this.flows, this.fields, this.apps, this.groups, this.tables)
    },
    appMap() {
      return _.keyBy(this.$store.state.application.items, 'id')
    },

    flows() {
      return _.map(this.$store.state.flow.items, f => ({
        id: f.id,
        type: this.$i18n.tc('FLOW', 2),
        name: f.name,
        detail: this.appMap[f.application]?.name,
        route: { name: 'app-flow', params: { appId: f.application, flowId: f.id, mode: 'code' } }
      }))
    },
    fields() {
      return _.map(this.$store.state.field.items, f => ({
        id: f.id,
        type: this.$i18n.tc('FIELD', 2),
        name: f.name,
        detail: f.path,

        route: { name: 'app-field', params: { appId: f.application, fieldId: f.id } }
      }))
    },
    apps() {
      return _.map(this.$store.state.application.items, a => ({
        id: a.id,
        type: this.$i18n.tc('APPLICATION', 2),
        name: a.name,
        detail: this.$i18n.t(a.type),
        route: { name: 'app-settings', params: { appId: a.id } }
      }))
    },
    groups() {
      return _.map(this.$store.state.group.items, g => ({
        id: g.id,
        type: this.$i18n.tc('GROUP', 2),
        name: g.name,
        detail: g.description,
        route: { name: 'group', params: { groupId: g.id } }
      }))
    },
    tables() {
      return _.map(this.$store.state.table.items, t => ({
        id: t.id,
        type: this.$i18n.tc('TABLE', 2),
        name: t.name,
        detail: '',
        route: { name: 'table-home', params: { tableId: t.id } }
      }))
    }
  },

  methods: {
    goto() {}
  },

  created() {
    Flow.readAllFlows()
    Field.readFields()
    Application.readAllApps()
    Group.readAllGroups()
    Table.readAllTables()

    User.currentUser().then(u => (this.lists = u.preferences?.landingPage || []))
  }
}
</script>
